// "AI shrnutí" card for the customer and machine detail pages. Nothing is sent to Claude until the user asks
// (each summary costs money); the result stays in component state and can be regenerated with "Obnovit".
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, ChevronDown, RefreshCw, AlertCircle, ArrowRight } from 'lucide-react';
import { aiApi } from '../../utils/aiApi';

const ACCENT = '#E53935';

const SEVERITY = {
  critical: { color: '#D32F2F', label: 'Kritické' },
  warning: { color: '#F9A825', label: 'Upozornění' },
  info: { color: '#1976D2', label: 'Informace' },
};

const RECORD_LINKS = {
  customer: { path: (id) => `/customers/${id}`, label: 'Zákazník' },
  machine: { path: (id) => `/machines/${id}`, label: 'Trenažér' },
  order: { path: () => '/orders', label: 'Objednávky' },
  delivery: { path: (id) => `/odvozy/${id}`, label: 'Odvoz' },
};

const formatTime = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('cs-CZ', { day: 'numeric', month: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const styles = {
  card: { backgroundColor: '#FFFFFF', border: '1px solid #E0E0E0', borderRadius: '12px', overflow: 'hidden' },
  header: {
    display: 'flex', alignItems: 'center', gap: '10px', width: '100%', padding: '14px 16px', background: 'none', border: 'none',
    cursor: 'pointer', textAlign: 'left', font: 'inherit', color: '#333333',
  },
  iconBox: { width: 32, height: 32, borderRadius: 8, backgroundColor: '#FFEBEE', color: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  title: { fontSize: '15px', fontWeight: 600, margin: 0 },
  note: { fontSize: '12px', color: '#888888', margin: 0 },
  body: { padding: '0 16px 16px', display: 'flex', flexDirection: 'column', gap: '14px' },
  primaryBtn: {
    display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '9px 16px', borderRadius: '8px', border: 'none',
    backgroundColor: ACCENT, color: '#FFFFFF', fontSize: '14px', fontWeight: 600, cursor: 'pointer', minHeight: 40,
  },
  ghostBtn: {
    display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 10px', borderRadius: '8px', border: '1px solid #E0E0E0',
    backgroundColor: '#FFFFFF', color: '#555555', fontSize: '13px', cursor: 'pointer', minHeight: 32,
  },
  sectionTitle: { fontSize: '12px', fontWeight: 600, color: '#888888', textTransform: 'uppercase', letterSpacing: '0.04em', margin: '0 0 6px' },
  list: { margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '6px' },
  error: { display: 'flex', gap: '8px', alignItems: 'flex-start', padding: '10px 12px', borderRadius: '8px', backgroundColor: '#FFEBEE', color: '#C62828', fontSize: '13px' },
  recordLink: { display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '12px', color: ACCENT, textDecoration: 'none', whiteSpace: 'nowrap' },
};

const SKELETON_CSS = `
@keyframes aiSummaryPulse { 0%, 100% { opacity: 1 } 50% { opacity: .45 } }
.ai-summary-skeleton { background: #EEEEEE; border-radius: 6px; animation: aiSummaryPulse 1.4s ease-in-out infinite; }
@media (prefers-reduced-motion: reduce) { .ai-summary-skeleton { animation: none; } }
`;

function Skeleton() {
  return (
    <div aria-hidden="true" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <style>{SKELETON_CSS}</style>
      {[70, 100, 92, 60].map((w, i) => (
        <div key={i} className="ai-summary-skeleton" style={{ height: i === 0 ? 18 : 12, width: `${w}%` }} />
      ))}
    </div>
  );
}

function RecordLink({ link }) {
  const def = link && RECORD_LINKS[link.type];
  if (!def) return null;
  return (
    <Link to={def.path(link.id)} style={styles.recordLink}>
      {def.label}<ArrowRight size={12} aria-hidden="true" />
    </Link>
  );
}

function OpenItems({ items }) {
  if (!items.length) return null;
  return (
    <section>
      <h4 style={styles.sectionTitle}>Otevřené body</h4>
      <ul style={styles.list}>
        {items.map((item, i) => {
          const sev = SEVERITY[item.severity] || SEVERITY.info;
          return (
            <li key={i} style={{ display: 'flex', gap: '8px', alignItems: 'baseline', fontSize: '14px', color: '#333333' }}>
              <span title={sev.label} aria-label={sev.label} style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: sev.color, flexShrink: 0, transform: 'translateY(-1px)' }} />
              <span style={{ flex: 1, minWidth: 0 }}>{item.text}</span>
              <RecordLink link={item.link} />
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function TextList({ title, items, marker }) {
  if (!items.length) return null;
  return (
    <section>
      <h4 style={styles.sectionTitle}>{title}</h4>
      <ul style={styles.list}>
        {items.map((text, i) => (
          <li key={i} style={{ display: 'flex', gap: '8px', fontSize: '14px', color: '#444444' }}>
            <span aria-hidden="true" style={{ color: '#AAAAAA' }}>{marker}</span>
            <span style={{ flex: 1, minWidth: 0 }}>{text}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function SummaryView({ summary }) {
  return (
    <>
      <div>
        <p style={{ fontSize: '16px', fontWeight: 600, color: '#222222', margin: '0 0 6px' }}>{summary.headline}</p>
        <p style={{ fontSize: '14px', lineHeight: 1.55, color: '#444444', margin: 0 }}>{summary.overview}</p>
      </div>
      <OpenItems items={summary.openItems || []} />
      <TextList title="Další kroky" items={summary.nextSteps || []} marker="→" />
      <TextList title="Chybějící nebo nesouhlasící údaje" items={summary.dataGaps || []} marker="!" />
    </>
  );
}

export default function AiSummaryCard({ kind, id }) {
  const recordKey = `${kind}:${id}`;
  const [open, setOpen] = useState(true);
  const [state, setState] = useState({ key: null, loading: false, error: '', result: null });
  const pendingKey = useRef(null);

  // state belongs to one record; after navigating to another record it is ignored until regenerated
  const current = state.key === recordKey ? state : { loading: false, error: '', result: null };
  const { loading, error, result } = current;

  const generate = async () => {
    const previous = result || null;
    pendingKey.current = recordKey;
    setOpen(true);
    setState({ key: recordKey, loading: true, error: '', result: previous });
    try {
      const path = kind === 'machine' ? `/insights/summary/machine/${id}` : `/insights/summary/customer/${id}`;
      const data = await aiApi(path, { method: 'POST' });
      if (pendingKey.current === recordKey) setState({ key: recordKey, loading: false, error: '', result: data });
    } catch (err) {
      if (pendingKey.current === recordKey) setState({ key: recordKey, loading: false, error: err.message || 'Shrnutí se nepodařilo vytvořit.', result: previous });
    }
  };

  const bodyId = `ai-summary-${kind}-${id}`;

  return (
    <div style={styles.card}>
      <button type="button" style={styles.header} onClick={() => setOpen((v) => !v)} aria-expanded={open} aria-controls={bodyId}>
        <span style={styles.iconBox}><Sparkles size={17} aria-hidden="true" /></span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ ...styles.title, display: 'block' }}>AI shrnutí</span>
          <span style={{ ...styles.note, display: 'block' }}>
            Claude{result?.generatedAt ? ` · vytvořeno ${formatTime(result.generatedAt)}` : ' · jen ze zapsaných údajů v ERP'}
          </span>
        </span>
        <ChevronDown size={18} aria-hidden="true" style={{ color: '#888888', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 160ms ease-out' }} />
      </button>

      {open && (
        <div id={bodyId} style={styles.body} aria-busy={loading}>
          {error && (
            <div role="alert" style={styles.error}>
              <AlertCircle size={16} aria-hidden="true" style={{ flexShrink: 0, marginTop: 1 }} />
              <span>{error}</span>
            </div>
          )}

          {loading && <Skeleton />}

          {!loading && result?.summary && <SummaryView summary={result.summary} />}

          {!loading && !result && (
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px' }}>
              <button type="button" style={styles.primaryBtn} onClick={generate}>
                <Sparkles size={16} aria-hidden="true" /> Vytvořit shrnutí
              </button>
              <span style={{ fontSize: '13px', color: '#888888' }}>
                Přehled stavu, termínů, otevřených bodů a chybějících údajů.
              </span>
            </div>
          )}

          {!loading && result && (
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button type="button" style={styles.ghostBtn} onClick={generate}>
                <RefreshCw size={14} aria-hidden="true" /> Obnovit
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
